// Curated freeze/holiday range suggestions per region (v226).
//
// Used by the "Suggest dates" popover next to each Break row in the
// Project Conditions flyout. Year-aware — pass `year` to materialise
// concrete ISO dates so PMs can apply them with one click without
// looking up calendars.
//
// Why static (not a live API)?
//   v1 covers the 95% case (Christmas/NY, Easter, Lunar New Year,
//   Independence/National days, regional bank holidays). A dynamic
//   API integration (Nager.Date) is already on the P3 backlog;
//   when that lands, swap `getRegionSuggestions` for a fetched
//   list. UI doesn't change.
//
// Date math:
//   Most entries are fixed-date (Christmas, July 4, etc.) so we just
//   format `YYYY-MM-DD`. Easter & Lunar New Year are computed: Easter
//   uses the Gauss algorithm; Lunar New Year is read from a precomputed
//   table covering 2024-2030 (extend as needed).

const _pad = (n) => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${_pad(m)}-${_pad(d)}`;

// ── Easter Sunday by Gauss algorithm ─────────────────────────────────
function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { y: year, m: month, d: day };
}

function shiftDate(y, m, d, days) {
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return { y: dt.getUTCFullYear(), m: dt.getUTCMonth() + 1, d: dt.getUTCDate() };
}

// ── Lunar New Year — precomputed Gregorian dates ─────────────────────
const LUNAR_NEW_YEAR = {
  2024: { m: 2, d: 10 },
  2025: { m: 1, d: 29 },
  2026: { m: 2, d: 17 },
  2027: { m: 2, d: 6 },
  2028: { m: 1, d: 26 },
  2029: { m: 2, d: 13 },
  2030: { m: 2, d: 3 },
};

// ── Suggestion factories — each returns `{label, start_date, end_date}`
function christmasFreeze(y) {
  return {
    label: 'Christmas / New Year freeze',
    start_date: ymd(y, 12, 22),
    end_date: ymd(y + 1, 1, 2),
  };
}

function thanksgivingFreezeUS(y) {
  // Thanksgiving = 4th Thursday of November → freeze covers the long weekend.
  const nov1 = new Date(Date.UTC(y, 10, 1));
  const dow = nov1.getUTCDay();
  const firstThu = ((11 - dow) % 7) + 1;
  const tgDay = firstThu + 21;
  const start = shiftDate(y, 11, tgDay, -1);  // Wed before
  const end = shiftDate(y, 11, tgDay, 3);    // Sun after
  return {
    label: 'Thanksgiving freeze (US)',
    start_date: ymd(start.y, start.m, start.d),
    end_date: ymd(end.y, end.m, end.d),
  };
}

function easterFreeze(y) {
  const e = easterSunday(y);
  const start = shiftDate(e.y, e.m, e.d, -2); // Good Friday
  const end = shiftDate(e.y, e.m, e.d, 1);    // Easter Monday
  return {
    label: 'Easter weekend',
    start_date: ymd(start.y, start.m, start.d),
    end_date: ymd(end.y, end.m, end.d),
  };
}

function lunarNewYear(y) {
  const lny = LUNAR_NEW_YEAR[y];
  if (!lny) return null;
  const start = shiftDate(y, lny.m, lny.d, -1);
  const end = shiftDate(y, lny.m, lny.d, 6); // typical week-long observance
  return {
    label: 'Lunar New Year',
    start_date: ymd(start.y, start.m, start.d),
    end_date: ymd(end.y, end.m, end.d),
  };
}

function fixed(label, y, m, d, lengthDays = 0) {
  const start = { y, m, d };
  const end = lengthDays > 0 ? shiftDate(y, m, d, lengthDays) : start;
  return {
    label,
    start_date: ymd(start.y, start.m, start.d),
    end_date: ymd(end.y, end.m, end.d),
  };
}

// ── Per-region suggestion lists ──────────────────────────────────────
function _baseSuggestions(y) {
  return [christmasFreeze(y), easterFreeze(y)].filter(Boolean);
}

const REGION_SUGGESTIONS = {
  US: (y) => [
    christmasFreeze(y),
    thanksgivingFreezeUS(y),
    fixed('Independence Day', y, 7, 4),
    fixed('Memorial Day weekend', y, 5, 25, 2),
    fixed('Labor Day weekend', y, 9, 1, 2),
    easterFreeze(y),
  ],
  CA: (y) => [
    christmasFreeze(y),
    fixed('Canada Day', y, 7, 1),
    fixed('Thanksgiving (Canada)', y, 10, 13),
    easterFreeze(y),
  ],
  UK: (y) => [
    christmasFreeze(y),
    easterFreeze(y),
    fixed('May Day bank holiday', y, 5, 5),
    fixed('Spring bank holiday', y, 5, 26),
    fixed('Summer bank holiday', y, 8, 25),
  ],
  AU: (y) => [
    christmasFreeze(y),
    fixed('Australia Day', y, 1, 26),
    fixed('Anzac Day', y, 4, 25),
    easterFreeze(y),
  ],
  IN: (y) => [
    christmasFreeze(y),
    fixed('Republic Day', y, 1, 26),
    fixed('Independence Day (India)', y, 8, 15),
    fixed('Diwali week', y, 11, 1, 4),
    lunarNewYear(y),
  ].filter(Boolean),
  DE: (y) => [
    christmasFreeze(y),
    easterFreeze(y),
    fixed('Labour Day', y, 5, 1),
    fixed('German Unity Day', y, 10, 3),
  ],
  FR: (y) => [
    christmasFreeze(y),
    easterFreeze(y),
    fixed('Bastille Day', y, 7, 14),
    fixed('Assumption Day', y, 8, 15),
    fixed('All Saints', y, 11, 1),
  ],
  JP: (y) => [
    christmasFreeze(y),
    fixed('Golden Week', y, 4, 29, 7),
    fixed('Obon week', y, 8, 13, 4),
    lunarNewYear(y),
  ].filter(Boolean),
  EU: (y) => [
    christmasFreeze(y),
    easterFreeze(y),
    fixed('Labour Day', y, 5, 1),
    fixed('All Saints', y, 11, 1),
  ],
  GLOBAL: (y) => [
    christmasFreeze(y),
    easterFreeze(y),
    lunarNewYear(y),
  ].filter(Boolean),
  NONE: _baseSuggestions,
};

/**
 * Get curated date-range suggestions for a region & year.
 *
 * @param {string} region One of HOLIDAY_REGIONS values, or null/undefined.
 * @param {number} year   4-digit year. Falls back to current year if invalid.
 * @returns {Array<{label, start_date, end_date}>} Always returns ≥1 entry.
 */
export function getRegionSuggestions(region, year) {
  const y = Number.isInteger(year) && year >= 1990 && year <= 2099
    ? year
    : new Date().getFullYear();
  const fn = REGION_SUGGESTIONS[region] || _baseSuggestions;
  return fn(y);
}

/**
 * Best-effort year extraction from an estimate's project conditions.
 * Reads `goal_start_date` first (when set), else `goal_go_live_date`,
 * else falls back to current year. Used to make the popover's
 * suggestions year-relevant for the project at hand.
 *
 * @param {object} conditions Estimate's `project_conditions` object
 * @returns {number} 4-digit year
 */
export function getProjectYear(conditions) {
  const candidates = [
    conditions?.goal_start_date,
    conditions?.goal_go_live_date,
  ];
  for (const iso of candidates) {
    if (typeof iso === 'string') {
      const y = parseInt(iso.slice(0, 4), 10);
      if (Number.isInteger(y) && y >= 1990 && y <= 2099) return y;
    }
  }
  return new Date().getFullYear();
}
